import { useState, useRef, useCallback } from 'react';
import { Upload, X, FileText, Film, Loader2 } from 'lucide-react';
import api from '../lib/api';

export default function FileUpload({ onUpload, folder = 'general', multiple = false, accept = 'image/*', label = 'Upload File', existingFiles = [], type = 'image' }) {
  const [files, setFiles] = useState(existingFiles?.filter(Boolean) || []);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const inputRef = useRef(null);

  const isVideo = type === 'video';
  const acceptStr = isVideo ? 'video/mp4,video/quicktime,video/x-msvideo,video/*' : accept;

  const handleFiles = useCallback(async (fileList) => {
    const toUpload = Array.from(fileList);
    if (!multiple && toUpload.length > 1) toUpload.length = 1;
    setUploading(true);
    setProgress(0);
    const uploaded = [];
    for (let i = 0; i < toUpload.length; i++) {
      const file = toUpload[i];
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('folder', folder);
        const endpoint = isVideo ? '/upload/video' : '/upload';
        const res = await api.post(endpoint, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        uploaded.push(res.data.url);
        setProgress(Math.round(((i + 1) / toUpload.length) * 100));
      } catch (err) {
        console.error('Upload failed:', err);
      }
    }
    const updated = multiple ? [...files, ...uploaded] : uploaded.length > 0 ? [uploaded[0]] : files;
    setFiles(updated);
    setUploading(false);
    setProgress(0);
    if (onUpload) onUpload(multiple ? updated : updated[0] || '');
  }, [files, folder, multiple, onUpload, isVideo]);

  const handleDrop = (e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); };
  const removeFile = (idx) => {
    const updated = files.filter((_, i) => i !== idx);
    setFiles(updated);
    if (onUpload) onUpload(multiple ? updated : updated[0] || '');
  };

  const isImageUrl = (url) => /\.(jpg|jpeg|png|gif|webp|svg)/i.test(url) || (url.includes('/images/') && !url.includes('.pdf'));
  const isVideoUrl = (url) => /\.(mp4|mov|avi|webm)/i.test(url) || url.includes('cloudinary') || url.includes('/video/');

  return (
    <div data-testid="file-upload" className="mt-1">
      <div onClick={() => !uploading && inputRef.current?.click()} onDrop={handleDrop} onDragOver={e => e.preventDefault()}
        className={`border-2 border-dashed rounded-xl p-5 text-center cursor-pointer transition-colors ${uploading ? 'border-primary/50 bg-primary/5' : 'border-border hover:border-primary/50'}`}>
        <input ref={inputRef} type="file" accept={acceptStr} multiple={multiple} onChange={(e) => handleFiles(e.target.files)} className="hidden" />
        {uploading ? (
          <div><Loader2 className="h-6 w-6 mx-auto text-primary animate-spin mb-2" /><p className="text-sm text-primary font-medium">Uploading... {progress}%</p></div>
        ) : (
          <div>
            {isVideo ? <Film className="h-6 w-6 mx-auto text-muted-foreground mb-2" /> : <Upload className="h-6 w-6 mx-auto text-muted-foreground mb-2" />}
            <p className="text-sm font-medium">{label}</p>
            <p className="text-xs text-muted-foreground mt-1">Drag & drop or click to browse</p>
          </div>
        )}
      </div>
      {files.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {files.map((url, i) => (
            <div key={i} className="relative group">
              {isVideoUrl(url) ? (
                <video src={url} className="w-24 h-20 rounded-lg object-cover border" muted />
              ) : isImageUrl(url) ? (
                <img src={url} alt="" className="w-20 h-20 rounded-lg object-cover border" onError={(e) => { e.target.style.display = 'none'; }} />
              ) : (
                <div className="w-20 h-20 rounded-lg border flex items-center justify-center bg-muted"><FileText className="h-6 w-6 text-muted-foreground" /></div>
              )}
              <button onClick={(e) => { e.stopPropagation(); removeFile(i); }}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-destructive text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
